import useTrackFilterVendors from '@hooks/useTrackFilterVendors';
import type { FilterConfig } from '@pages/VendorsSearch/containers/FilterPills/atoms';
import { useUpdateFilterValues } from '@pages/VendorsSearch/containers/FilterPills/hooks/use-update-filter-values';
import type { FacetMultiSelectFilterStrategy } from '@pages/VendorsSearch/containers/FilterPills/strategies/facet-multi-select-filter-strategy';
import { useAppSelector } from '@redux/hooks';
import React, { type FunctionComponent, useMemo } from 'react';
import PillWithDropdown from '../../../../../components/shared/PillWithDropdown';
import { labels, subtext } from '../../utils';

export interface FacetMultiSelectFilterProps {
	filterConfig: FilterConfig;
	appliedFilter?: FacetMultiSelectFilterStrategy;
}

export const FacetMultiSelectFilter: FunctionComponent<
	FacetMultiSelectFilterProps
> = ({ filterConfig, appliedFilter }) => {
	const { updateFilterValues, removeFilterValue } = useUpdateFilterValues();
	const displayMarketCode = useAppSelector(
		(state) => state.location.marketCode,
	);
	const isMobile = useAppSelector((state) => state.viewport.isMobile);
	const [trackVendors] = useTrackFilterVendors({ displayMarketCode });
	const selectedOptions = useMemo(
		() => (appliedFilter ? appliedFilter.getAppliedFacetIds() : []),
		[appliedFilter],
	);

	const options = useMemo(
		() =>
			filterConfig.filterOptions.map((o) => ({
				name: o.name,
				value: o.id,
				subtext: subtext[o.id as keyof typeof subtext],
			})) || [],
		[filterConfig.filterOptions],
	);

	const onSave = (filtersToApply: string[]) => {
		const selectedOptions = filtersToApply.reduce(
			(acc, facetId) => {
				const selectedOption = filterConfig?.filterOptions.find(
					(option) => option.id === facetId,
				);
				if (selectedOption) {
					acc.push({ facetId });
				}
				return acc;
			},
			[] as { facetId: string }[],
		);

		if (selectedOptions.length === 0) {
			removeFilterValue(filterConfig.id);
		} else {
			updateFilterValues([{ id: filterConfig.id, value: selectedOptions }]);
		}
		trackVendors('save selected filters');
	};

	const onClear = () => {
		removeFilterValue(filterConfig.id);
		trackVendors('clear selected filters');
	};

	const pillLabel =
		labels[filterConfig.name as keyof typeof labels] || filterConfig.name;

	return (
		<PillWithDropdown
			isMobile={isMobile}
			onClearOptions={onClear}
			onSave={onSave}
			pillLabel={pillLabel}
			appliedOptions={selectedOptions}
			options={options}
			singleSelect={false}
		/>
	);
};
